
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { SupplierModel } from '@/models/supplier';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { ReportModel } from "@/models/report";
import { FilterColumnList } from "@/collections/filter_columns";
import { FilterColumnModel } from '@/models/filter_column';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { fetchSupplierCostMap, fetchExchangeRate } from '@/helpers/poCostResolver';
import FeatureLockedNotify from '@/components/FeatureLockedNotify.vue';
import { StoreModel } from '@/models/store';
import DateFilter from '@/components/DateFilter.vue';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { FEATURES } from '@/config/featureConfig';
import ForecastingMethodSelector from '@/components/ForecastingMethodSelector.vue';
import LocationFilter from '@/components/LocationFilter.vue';
import { EventBus } from "@/main";
import { reviewRequestService } from '@/services/ReviewRequestService';
import DataSyncStatusBanner from '@/components/DataSyncStatusBanner.vue';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import { applyTableSelectionDiff } from '@/utils/purchase-order/selectionMapOps';
import AddToPurchaseListDialog from '@/components/purchase-order/AddToPurchaseListDialog.vue';
import { supplierProductsStrategy } from '@/utils/purchase-order/strategies/supplierProductsStrategy';
import {
  getCreateViewConfig,
  saveCreateViewConfig,
  getCustomColumnDefs,
  defaultCreateViewConfig,
  CreateViewColumnConfig,
} from '@/services/createViewColumnConfig';
import { buildCreateHeaders, customConfigKey, REPORT_ONLY_SORT_VALUES } from '@/utils/purchase-order/createViewHeaders';
import { REORDER_PARAM_COLUMNS, formatReorderParamCell } from '@/utils/purchase-order/reorderParamColumns';
import { fetchCustomColumnValuesByVariant } from '@/utils/purchase-order/fetchCustomColumnValuesByVariant';
import { buildCreateReportColumns } from '@/utils/purchase-order/createReportColumns';
import { buildFastProductRow } from '@/utils/purchase-order/buildFastProductRow';
import { buildSearchAddedRows } from '@/utils/purchase-order/buildSearchAddedRow';
import { pinSelectedFirst } from '@/utils/purchase-order/pinSelectedFirst';
import { mergeReorderSuggestion } from '@/utils/purchase-order/mergeReorderSuggestion';
import AddProductsDialog from '@/components/purchase-order/AddProductsDialog.vue';
import { isUsableImageUrl } from '@/helpers/variantImage';


@Component({
  components: {
    FeatureLockedNotify,
    DateFilter,
    ForecastingMethodSelector,
    LocationFilter,
    DataSyncStatusBanner,
    CopyLinkButton,
    AddToPurchaseListDialog,
    AddProductsDialog,
  }
})
export default class PurchaseOrderCreateView extends Vue {
  /**
   * The usable-image rule, exposed so the template can call it. A Vue class
   * component cannot call a module function from a template unless it is a
   * class property — without this the v-if evaluates `undefined` as a
   * function and Vue warns at runtime while the build stays green.
   */
  private isUsableImageUrl = isUsableImageUrl;

  private static readonly REASSURE_KEY = 'po-create-selection-reassured';
  // US-090: stable empty binding for the closed Add Products dialog — see the template.
  private readonly NO_VARIANT_IDS: number[] = [];
  private shouldReassureOnNextFetch = false;

  private CurrencyHelper = CurrencyHelper;
  private supplier: SupplierModel | null = null;
  private purchaseOrder: PurchaseOrderModel = new PurchaseOrderModel({});
  private availableProducts: any[] = [];
  // Source of truth for selected items, survives pagination/search/filter.
  // `selectedProducts` and `tableSelection` below are derived views.
  private selectionMap: Map<number, any> = new Map();
  // Reactive version counter. Vue 2 does NOT track `Map.set/delete/clear`
  // mutations, so any consumer of `selectedProducts` (Order Summary counter,
  // future toolbar chip, etc.) must also depend on this counter to
  // re-render. Bumped after every Map mutation.
  private selectionVersion = 0;
  private search = '';
  private submitting = false;
  private filterType = 'all';
  private loadingAvailableProduct = false;
  private showDetails = true;
  private orderDateMenu = false;
  private expectedDeliveryDateMenu = false;
  private dates: string[] = [];
  private defaultRange = '30d';
  private showNeedReorderNowOnly = false;
  private showClearSelectionConfirm = false;
  private addToPurchaseListOpen = false;
  // US-090: the "Add Products" search dialog, for putting a known product on the PO by
  // name/SKU/barcode instead of hunting for it in the suggestion list.
  private addProductsOpen = false;
  private isCreated: boolean = false;
  private activeLocationFilters: FilterColumnList | null = null;
  private loadingLocations = false;
  private locations: StoreLocationModel[] = [];
  private storeLocationList = new StoreLocationList();
  private productColumn:string = 'Item[Item Description]';
  // US-072: create-view column config (own scope, cross-device via UserConfigs).
  // Initialized to the render fallback so the table shows before the fetch lands.
  private createViewConfig: CreateViewColumnConfig = defaultCreateViewConfig();
  // Product custom-column definitions: { columnId: { label, enabled, order } }.
  private customColumnDefs: Record<string, { label: string; enabled: boolean; order: number }> = {};
  // Custom-column values, read-only, keyed variant_id -> columnId -> string.
  private customColumnValues: Record<string, Record<string, string>> = {};
  // Monotonic token guarding loadCustomColumnValues against overlapping fetches.
  private customValuesFetchToken = 0;
  private manageColumnsDialog = false;
  private savingColumns = false;
  // Working copy edited inside the Manage Columns dialog (committed on Save).
  private columnDraft: Record<string, { enabled: boolean; order: number }> = {};
  private readonly formatReorderParamCell = formatReorderParamCell;
  private shippingCost: number = 0;
  private shippingTaxRate: number = 0;
  private discountAmount: number = 0;
  private discountType: 'fixed' | 'percentage' = 'fixed';
  private adjustmentAmount: number = 0;
  private adjustmentNote: string = '';
  private taxTypeList: TaxTypeList = new TaxTypeList();
  // AS-10746: set when the tax-type list could not be loaded, so the page can
  // say the PO will be created untaxed instead of failing silently.
  private taxTypesUnavailable = false;
  private exchangeRate: number | null = null;
  private supplierDefaultTaxType: TaxTypeModel | null = null;
  private supplierCostMap: Map<number, number | null> = new Map();


  // New properties for forecast settings - REMOVED as they are now in child component
  // private forecastSetting: ForecastSetting = new ForecastSetting();
  // private loadingForecastSettings: boolean = false;
  // private selectedForecastingMethod: string | null = null;

  private dataTableOptions: any = {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['Item[Item Description]'],
    sortDesc: [false],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false
  };
  private totalAvailableProducts = 0;
  // US-087: incremented on every refreshProducts() call; Phase 1/Phase 2 responses
  // whose captured token no longer matches this are stale (superseded by a newer
  // page/sort/search/filter change) and must not be applied.
  private productsRequestToken = 0;
  // US-087: true once the current page's Phase 2 (or the report-only fallback) has
  // resolved. Gates the "Needs reorder now" toggle and the date-range filter, which
  // both depend on forecast data Phase 1 doesn't have.
  private reorderSuggestionsLoaded = false;

  // US-087: true while the current page's reorder-suggestion data is still loading —
  // gates controls that only make sense once forecast data has arrived.
  get reorderFiltersDisabled(): boolean {
    return !this.reorderSuggestionsLoaded;
  }

  // US-087: true when the view needs data Phase 1 (the fast product-list endpoint)
  // structurally cannot provide — "Needs reorder now" is a forecast concept, and the
  // fast endpoint's controller has no filters param at all, so an active location
  // filter can't be honored by it either. refreshProducts() routes both cases to the
  // single-report fallback (fetchProductsViaReportOnly); the headers getter below uses
  // the same flag to re-enable sorting on the report-only columns in that mode, since
  // only the full report query can actually serve a sort on them.
  get isReportOnlyFlow(): boolean {
    return this.showNeedReorderNowOnly ||
      (this.activeLocationFilters !== null && this.activeLocationFilters.items.length > 0);
  }

  get headers() {
    return buildCreateHeaders(this.createViewConfig, {
      productColumn: this.productColumn,
      currSuffix: this.currSuffix,
      customColumnDefs: this.customColumnDefs,
      reportOnlySortable: this.isReportOnlyFlow,
    });
  }

  // US-072: read-only dynamic columns needing a formatted cell slot.
  get reorderParamHeaders() {
    return this.headers.filter((h: any) => h.type === 'reorder_param');
  }
  get customColumnHeaders() {
    return this.headers.filter((h: any) => h.type === 'custom');
  }

  // US-072: Manage-Columns dialog, grouped. Each column's `key` is its create-view
  // config id; this is also the single list openManageColumns() seeds the draft
  // from, so every rendered checkbox is guaranteed a draft entry.
  get manageSections() {
    const sections: Array<{ title: string; cols: Array<{ key: string; label: string }> }> = [
      {
        title: 'Columns',
        cols: [
          { key: 'image', label: 'Image' },
          { key: 'historical_sales', label: 'Historical Sales' },
          { key: 'reorder_date', label: 'Reorder Date' },
          { key: 'suggested_qty', label: 'Suggested Qty' },
        ],
      },
      {
        title: 'Reorder Parameters',
        cols: REORDER_PARAM_COLUMNS.map((c) => ({ key: c.id, label: c.label })),
      },
    ];
    const custom = Object.keys(this.customColumnDefs).map((id) => ({ key: customConfigKey(id), label: this.customColumnDefs[id].label }));
    if (custom.length) { sections.push({ title: 'Custom Columns', cols: custom }); }
    return sections;
  }
  // True once openManageColumns() has seeded the editable draft. Guards the
  // dialog body so its checkboxes never bind to an undefined draft entry (the
  // dialog body renders eagerly in this Vuetify version).
  get columnDraftReady() {
    return Object.keys(this.columnDraft).length > 0;
  }

  // US-072: load the create-view column config + custom-column definitions.
  // Both services return a usable value on failure (config -> render fallback,
  // defs -> {}), so no try/catch is needed here.
  private async loadCreateViewColumns() {
    const [config, defs] = await Promise.all([
      getCreateViewConfig(),
      getCustomColumnDefs(),
    ]);
    this.createViewConfig = config;
    this.customColumnDefs = defs;
  }

  // US-072: fetch read-only custom-column values for the currently visible rows,
  // only when at least one custom column is enabled for this scope. Guarded by a
  // request token so a slow older fetch can't overwrite a newer page's values.
  private async loadCustomColumnValues() {
    const hasEnabledCustom = this.customColumnHeaders.length > 0;
    if (!hasEnabledCustom) {
      this.customColumnValues = {};
      return;
    }
    const variantIds = this.availableProducts
      .map((p: any) => Number(p.variant_id))
      .filter((id: number) => Number.isFinite(id));
    if (variantIds.length === 0) {
      return;
    }
    const token = ++this.customValuesFetchToken;
    try {
      const values = await fetchCustomColumnValuesByVariant(variantIds);
      // Ignore a stale response superseded by a newer fetch.
      if (token !== this.customValuesFetchToken) { return; }
      this.customColumnValues = values;
    } catch (err) {
      if (token !== this.customValuesFetchToken) { return; }
      console.error('[US-072] Failed to load custom column values:', err);
      EventBus.$emit('show-snackbar', { message: 'Could not load custom column values.', color: 'error' });
    }
  }

  // Read a single custom-column cell value, read-only, keyed by variant_id.
  private getCustomCell(item: any, columnId: string): string {
    const values = this.customColumnValues[String(item?.variant_id)];
    return this.formatReorderParamCell(values ? values[columnId] : '');
  }

  private openManageColumns() {
    // Seed the editable draft from exactly the keys the dialog renders
    // (manageSections), so every checkbox is guaranteed a draft entry regardless
    // of what the saved config contains. Existing config values win where present.
    const cfg = this.createViewConfig.columns || {};
    const draft: Record<string, { enabled: boolean; order: number }> = {};
    let fallbackOrder = 20;
    this.manageSections.forEach((section) => {
      section.cols.forEach((col) => {
        const existing = cfg[col.key];
        draft[col.key] = existing
          ? { enabled: existing.enabled === true, order: existing.order }
          : { enabled: false, order: fallbackOrder++ };
      });
    });
    this.columnDraft = draft;
    this.manageColumnsDialog = true;
  }

  private async saveManageColumns() {
    this.savingColumns = true;
    try {
      // Merge the draft toggles over the existing config so required columns
      // (product/quantity/unit_cost/total) and their order are preserved.
      const columns: Record<string, { enabled: boolean; order: number }> = { ...this.createViewConfig.columns };
      Object.keys(this.columnDraft).forEach((id) => {
        columns[id] = { enabled: this.columnDraft[id].enabled === true, order: this.columnDraft[id].order };
      });
      const next: CreateViewColumnConfig = { columns, metafieldColumns: this.createViewConfig.metafieldColumns || {} };
      this.createViewConfig = await saveCreateViewConfig(next);
      this.manageColumnsDialog = false;
      await this.loadCustomColumnValues();
    } catch (err) {
      console.error('[US-072] Failed to save columns:', err);
      EventBus.$emit('show-snackbar', { message: 'Could not save column configuration.', color: 'error' });
    } finally {
      this.savingColumns = false;
    }
  }
  private requiredRule = (v: string) => !!v || 'Field is required';

  private rules = {
    quantity: [
      (v: number | string) => v !== '' && v !== null || 'Quantity is required',
      (v: number | string) => Number(v) > 0 || 'Quantity must be greater than 0'
    ],
    unitCost: [
       (v: number | string) => v !== '' && v !== null || 'Unit Cost is required',
       (v: number | string) => Number(v) >= 0 || 'Unit Cost must be non-negative'
    ]
  };

  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;

  private get canManagePurchaseOrders(): boolean {
    const validateValues = [
      {
        code: 'purchase_order_management',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  @Watch('dates')
  async onDatesChanged() {
    if (!this.isCreated) {
      return;
    }
    this.armReassurance();
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1;
    } else {
      await this.refreshProducts();
    }
  }

  @Watch('dataTableOptions')
  async onTableOptionsChanged(newValue: any, oldValue: any) {
    if (!this.isCreated) {
      return;
    }
    // Only fetch data if page or itemsPerPage changes. Sorting/filtering handled by other watchers or events.
    if (newValue.page !== oldValue.page || newValue.itemsPerPage !== oldValue.itemsPerPage ||
        JSON.stringify(newValue.sortBy) !== JSON.stringify(oldValue.sortBy) ||
        JSON.stringify(newValue.sortDesc) !== JSON.stringify(oldValue.sortDesc)
        ) {
      this.armReassurance();
      await this.refreshProducts();
    }
  }

  @Watch('selectionVersion')
  private onSelectionVersionChanged() {
    // If the user empties the selection while in 'Show selected only' mode,
    // the toolbar (and its toggle) hides itself — leaving them stuck in an
    // empty filtered view with no way out. Auto-revert to 'all' to restore
    // the normal product list.
    if (this.selectionMap.size === 0 && this.filterType === 'selected') {
      this.filterType = 'all';
    }
  }

  async created() {
    const supplierIdParam = this.$route.params.supplierId;
    if (supplierIdParam) {
      this.supplier = new SupplierModel({ id: Number(supplierIdParam) });
      await this.supplier.fetch();
    }
    this.initializePurchaseOrder();

    // Fetch cost map + exchange rate first (needed by refreshProducts). US-072: load
    // the column config here too so refreshProducts knows which reorder-param
    // measures to request (only the enabled ones).
    await Promise.all([
      this.fetchExchangeRate(),
      this.fetchSupplierCostMap(),
      this.loadCreateViewColumns(),
    ]);
    // Then fetch products in parallel with independent loads
    await Promise.all([
      this.supplier ? this.refreshProducts() : Promise.resolve(),
      this.fetchLocations(),
      this.taxTypeList.fetch().catch((e: any) => {
        // AS-10746: this used to be a bare console.warn. A failed fetch means
        // every line is created with no tax and no way to notice — and until
        // the tax-only endpoint shipped, no way to add it afterwards either.
        console.warn('Tax types unavailable:', e);
        this.taxTypesUnavailable = true;
      }),
    ]);

    // US-072: with config + products both loaded, fetch custom-column values
    // for the visible variants (only if a custom column is enabled).
    await this.loadCustomColumnValues();

    // Resolve supplier's default tax type
    this.resolveSupplierDefaultTaxType();

    this.isCreated = true;
  }

  private initializePurchaseOrder() {
    const baseOrderDateStr = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

    this.purchaseOrder = new PurchaseOrderModel({
      poNumber: this.generatePoNumber(),
      orderDate: baseOrderDateStr,
      supplierId: this.supplier?.id,
      supplierName: this.supplier?.name,
      currencyCode: this.supplierCurrencyCode,
    });

    // Calculate and set expectedDeliveryDate based on supplier lead time
    if (this.supplier && typeof this.supplier.leadTime === 'number' && this.supplier.leadTime >= 0) {
        const orderDate = new Date(baseOrderDateStr);
        const leadTimeDays = this.supplier.leadTime;
        const expectedDate = new Date(orderDate); // Create a new date object
        expectedDate.setDate(orderDate.getDate() + leadTimeDays);
        this.purchaseOrder.expectedDeliveryDate = expectedDate.toISOString().substr(0, 10);
    } else {
        // Set to undefined if supplier or leadTime is invalid/missing
        this.purchaseOrder.expectedDeliveryDate = undefined;
    }
  }

  private async fetchExchangeRate() {
    if (!this.isForeignCurrency) return;
    this.exchangeRate = await fetchExchangeRate(this.supplierCurrencyCode, this.storeCurrency);
  }

  private async fetchSupplierCostMap() {
    if (!this.supplier) return;
    this.supplierCostMap = await fetchSupplierCostMap(this.supplier.id);
  }

  private generatePoNumber(): string {
    const prefix = 'PO';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomNumber = Math.floor(Math.random() * 90000 + 10000); // 5-digit random number
    return `${prefix}${date}-${randomNumber}`;
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      await this.storeLocationList.fetch();
      this.locations = this.storeLocationList.items;
    } catch (error) {
      this.$emit('error', 'Failed to load store locations');
    } finally {
      this.loadingLocations = false;
    }
  }

  // US-087: ensure this.supplier matches the route's supplierId before any product
  // fetch. Mirrors the supplier-switch branch that used to live inline at the top of
  // fetchData() — pulled out so both the fast path and the report-only fallback
  // share it via refreshProducts() instead of duplicating it.
  private async ensureSupplierLoaded() {
    const supplierId = Number(this.$route.params.supplierId);
    if (this.supplier && this.supplier.id === supplierId) { return; }
    this.selectionMap.clear();
    this.selectionVersion++;
    this.supplier = new SupplierModel({ id: supplierId });
    await this.supplier.fetch();
    await Promise.all([
      this.fetchExchangeRate(),
      this.fetchSupplierCostMap(),
    ]);
    this.initializePurchaseOrder();
  }

  // US-087: the "already selected, reuse the stored row verbatim" check shared by
  // every path that builds/refreshes availableProducts rows (toFastRow and the
  // report-only fallback) — preserves in-progress edits across pagination/search/sort.
  private getExistingSelectionRow(variantId: number | string): any | undefined {
    return this.selectionMap.get(Number(variantId));
  }

  // US-087: after mergeReorderSuggestion() returns a replacement object, keep
  // selectionMap in sync too. toFastRow()/the report-only fallback hand out the
  // exact object stored in selectionMap for an already-selected row, so
  // availableProducts and selectionMap start out as the same reference; once a
  // merge replaces that reference in availableProducts without updating
  // selectionMap, submitPurchaseOrder(), subtotalValue, and the "selected"
  // filter (all reading selectionMap) keep serving the stale pre-merge row.
  private syncSelectionMapAfterMerge(row: any, merged: any): void {
    if (merged === row) { return; }
    const variantId = Number(row.variant_id);
    if (this.selectionMap.get(variantId) === row) {
      this.selectionMap.set(variantId, merged);
      this.selectionVersion++;
    }
  }

  // US-087: the cost-ladder options shared by every buildFastProductRow/
  // mergeReorderSuggestion call this component makes — only supplierCost varies per
  // row, so callers spread this and add that one field.
  private get costLadderCommonOpts() {
    return {
      exchangeRate: this.exchangeRate,
      isForeignCurrency: this.isForeignCurrency,
      supplierCurrencyCode: this.supplierCurrencyCode,
    };
  }

  // US-087: build (or reuse) one Phase-1 table row.
  private toFastRow(p: any): any {
    const existing = this.getExistingSelectionRow(p.variant_id);
    if (existing) { return existing; }
    return buildFastProductRow(p, {
      ...this.costLadderCommonOpts,
      supplierCost: this.supplierCostMap.get(Number(p.variant_id)) ?? null,
    });
  }

  // US-087 Phase 1: fast product list via the optimized supplier-products endpoint
  // (SupplierModel.fetchProductsOptimized, <200ms). Renders immediately; no forecast
  // data yet — quantities start at 0 and are editable right away.
  private async fetchProductsFast(token: number) {
    this.loadingAvailableProduct = true;
    try {
      const itemsPerPage = this.dataTableOptions.itemsPerPage === -1 ? 10000 : this.dataTableOptions.itemsPerPage;
      // US-087: a sort chosen while the report-only flow was active (e.g. Suggested
      // Qty) can still be sitting in dataTableOptions after transitioning back to
      // the fast flow, whose optimized endpoint doesn't support those columns —
      // fall back to the default rather than forwarding an unsupported sort key.
      const requestedSort = this.dataTableOptions.sortBy?.[0];
      const sortBy = requestedSort && !REPORT_ONLY_SORT_VALUES.has(requestedSort)
        ? requestedSort
        : 'Item[Item Description]';
      const result = await this.supplier!.fetchProductsOptimized({
        page: this.dataTableOptions.page,
        limit: itemsPerPage,
        search: this.search ? this.search.trim() : '',
        sortBy,
        sortOrder: this.dataTableOptions.sortDesc?.[0] ? 'desc' : 'asc',
        // Products excluded from replenishment must not be orderable. The
        // report-only flow below already drops them (the inventory_replenishment
        // cube filters on the flag), so without this the two paths disagree:
        // toggling "Needs reorder now" would change which products exist.
        excludeReplenishmentExcluded: true,
      });
      if (token !== this.productsRequestToken) { return; } // superseded by a newer request
      this.availableProducts = result.products.map((p: any) => this.toFastRow(p));
      this.totalAvailableProducts = result.pagination.total;
      if (this.isCreated) {
        await this.loadCustomColumnValues();
      }
    } catch (error) {
      this.$emit('error', 'Failed to load products for supplier');
    } finally {
      if (token === this.productsRequestToken) {
        this.loadingAvailableProduct = false;
      }
    }
  }

  // US-087: shared post-fetch bookkeeping (selection-persistence reassurance
  // snackbar). Called from both fetchProductsFast() [via refreshProducts()] and
  // fetchProductsViaReportOnly() so the "N items stay selected" message fires
  // regardless of which path is active — it's about selection persistence, which
  // Phase 1 already fully handles.
  private maybeShowReassurance() {
    if (this.shouldReassureOnNextFetch && this.selectedProducts.length > 0) {
      EventBus.$emit('show-snackbar', {
        message: `${this.selectedProducts.length} items stay selected across pages and filters.`,
        color: 'info',
      });
      sessionStorage.setItem(PurchaseOrderCreateView.REASSURE_KEY, '1');
    }
    this.shouldReassureOnNextFetch = false;
  }

  // US-087: build the FilterColumnList + reportOptions shared by both report-driven
  // fetches below (the fast path's background Phase 2, and the full report-only
  // fallback). Only the pieces that genuinely differ are parameters: whether to fold
  // in the active location filter (Phase 1 can't honor one, so only the fallback
  // needs it) and the "needs reorder now" toggle's value (always off for Phase 2,
  // since refreshProducts() routes that toggle to the fallback entirely).
  private buildReorderReportOptions(
    opts: { includeLocationFilters: boolean; showNeedReorderNowOnly: boolean },
  ): { filters: FilterColumnList; reportOptions: any } {
    const filters = new FilterColumnList();
    if (this.search && this.search.trim() !== '') {
      const searchFilter = new FilterColumnModel();
      searchFilter.mapData({
        code: 'Item[Item Description]',
        name: 'Product Search',
        value: [this.search.trim()],
        operator: 'contains',
        dataType: 'string'
      });
      filters.add(searchFilter);
    }
    if (opts.includeLocationFilters && this.activeLocationFilters && this.activeLocationFilters.items.length > 0) {
      this.activeLocationFilters.items.forEach(filter => {
        filters.add(filter);
      });
    }

    const pageOptions = {
      page: this.dataTableOptions.page,
      itemsPerPage: this.dataTableOptions.itemsPerPage === -1 ? 10000 : this.dataTableOptions.itemsPerPage,
      sortBy: this.dataTableOptions.sortBy,
      sortDesc: this.dataTableOptions.sortDesc
    };

    const reportOptions: any = {
      initialFilterColumns: filters,
      pageOptions: pageOptions,
      suggestFutureReorderQuantity: !opts.showNeedReorderNowOnly,
      // US-105: pass the urgent toggle as its own ROW-filter option (now
      // [days_to_reorder] <= 0), independent of the suggest-future param.
      showNeedReorderNowOnly: opts.showNeedReorderNowOnly,
      // US-072: request the default measures plus only the reorder-parameter
      // measures whose columns are enabled (reorderParamHeaders' value is the
      // report code), so we don't compute all ten on every fetch by default.
      columns: buildCreateReportColumns(this.reorderParamHeaders.map((h: any) => h.value)),
    };

    if (this.dates && this.dates.length >= 2) {
      reportOptions.dateRange = { start: this.dates[0], end: this.dates[1] };
    }

    return { filters, reportOptions };
  }

  // US-087: the original single-report-call flow, kept as the fallback for views
  // Phase 1 structurally cannot serve — "Needs reorder now" (a forecast concept) and
  // an active location filter (no filters param on the fast endpoint). Behaves
  // exactly as the pre-US-087 fetchData() did, including using the report as the
  // sole product source and its own total-count query. ensureSupplierLoaded() is
  // called by refreshProducts() before this, so no supplier-switch logic here.
  private async fetchProductsViaReportOnly(token: number) {
    this.loadingAvailableProduct = true;
    try {
      const { filters, reportOptions } = this.buildReorderReportOptions({
        includeLocationFilters: true,
        showNeedReorderNowOnly: this.showNeedReorderNowOnly,
      });

      const countReportOptions: any = {
        initialFilterColumns: filters,
        suggestFutureReorderQuantity: !this.showNeedReorderNowOnly,
        // US-105: keep the count query in lockstep with the row fetch.
        showNeedReorderNowOnly: this.showNeedReorderNowOnly
      };
      if (this.dates && this.dates.length >= 2) {
        countReportOptions.dateRange = { start: this.dates[0], end: this.dates[1] };
      }

      const [reportModel, countResponse]: [ReportModel, { totalItems: number } | any] = await Promise.all([
        this.supplier!.fetchProductsWithOptions(
          'inventory_replenishment',
          'eq',
          reportOptions
        ),
        this.supplier!.countProductsWithOptions(
          'inventory_replenishment',
          'eq',
          countReportOptions
        )
      ]);
      if (token !== this.productsRequestToken) { return; } // superseded

      // The report is the sole product source here, so every row is effectively a
      // fresh "suggestion" with no prior Phase-1 row to merge into — reuse the same
      // pure stamping logic Phase 2 uses by handing it a bare pending row, instead of
      // re-deriving the quantity/cost ladder inline a second time in this file.
      const commonOpts = this.costLadderCommonOpts;
      this.availableProducts = reportModel.getReportRowsData().map(p => {
        const existing = this.getExistingSelectionRow(p.variant_id);
        const baseRow = existing
          || { variant_id: p.variant_id, userEdited: false, suggestionState: 'pending' as const };
        const merged = mergeReorderSuggestion(baseRow, p, {
          ...commonOpts,
          supplierCost: this.supplierCostMap.get(Number(p.variant_id)) ?? null,
        });
        if (existing) {
          this.syncSelectionMapAfterMerge(existing, merged);
        }
        return merged;
      });
      this.totalAvailableProducts = countResponse.totalItems || 0;
      // US-072: on re-fetch (pagination/search/filter) refresh custom-column
      // values for the new page. During initial created() load isCreated is
      // still false and created() drives this call once, avoiding a double fetch.
      if (this.isCreated) {
        await this.loadCustomColumnValues();
      }
    } catch (error) {
      this.$emit('error', 'Failed to load products for supplier');
    } finally {
      if (token === this.productsRequestToken) {
        this.loadingAvailableProduct = false;
        // Re-enable the reorder-dependent controls (urgency toggle, date filter)
        // even when the report call fails — otherwise the view stays locked
        // until a full page reload, since nothing else clears the flag.
        this.reorderSuggestionsLoaded = true;
        this.maybeShowReassurance();
      }
    }
  }

  // US-087 Phase 2: background fetch of reorder suggestions for the current page,
  // fired after Phase 1 has already rendered. Merges into the existing rows by
  // variant_id instead of replacing the table — see mergeReorderSuggestionsIntoTable.
  // Not awaited by refreshProducts(); runs while the user is already looking at the
  // fast-loaded list.
  private async fetchReorderSuggestions(token: number) {
    try {
      // No location filters here: refreshProducts() routes any active location
      // filter to fetchProductsViaReportOnly() instead — Phase 1 (and therefore
      // this mirrored Phase 2 call) never runs while one is active.
      const { reportOptions } = this.buildReorderReportOptions({
        includeLocationFilters: false,
        showNeedReorderNowOnly: false,
      });

      const reportModel: ReportModel = await this.supplier!.fetchProductsWithOptions(
        'inventory_replenishment', 'eq', reportOptions
      );
      if (token !== this.productsRequestToken) { return; } // superseded

      this.mergeReorderSuggestionsIntoTable(reportModel.getReportRowsData());
      this.reorderSuggestionsLoaded = true;
      if (this.isCreated) {
        await this.loadCustomColumnValues();
      }
    } catch (error) {
      if (token !== this.productsRequestToken) { return; }
      this.availableProducts.forEach((row: any, index: number) => {
        if (row.suggestionState === 'pending') {
          this.$set(this.availableProducts, index, { ...row, suggestionState: 'unavailable' });
        }
      });
      this.reorderSuggestionsLoaded = true;
      EventBus.$emit('show-snackbar', {
        message: 'Reorder suggestions could not be loaded — quantities can still be entered manually.',
        color: 'warning',
      });
    }
  }

  // US-087: apply Phase 2's report rows onto the already-visible Phase-1 rows,
  // keyed by variant_id. Delegates the actual merge rule to the pure
  // mergeReorderSuggestion helper; only replaces a row in the reactive array when
  // something changed (reference inequality — see mergeReorderSuggestion's contract).
  private mergeReorderSuggestionsIntoTable(suggestionRows: any[]) {
    const byVariant = new Map<number, any>(suggestionRows.map((r: any) => [Number(r.variant_id), r]));
    const commonOpts = this.costLadderCommonOpts;
    this.availableProducts.forEach((row: any, index: number) => {
      const suggestion = byVariant.get(Number(row.variant_id));
      const merged = mergeReorderSuggestion(row, suggestion, {
        ...commonOpts,
        supplierCost: this.supplierCostMap.get(Number(row.variant_id)) ?? null,
      });
      if (merged !== row) {
        this.$set(this.availableProducts, index, merged);
        this.syncSelectionMapAfterMerge(row, merged);
      }
    });
  }

  // US-087: single entry point for every product-list trigger (initial load,
  // pagination, sort, search, location filter, date range, the urgency toggle, the
  // data-sync-ready event). Routes to the report-only fallback when the view needs
  // data Phase 1 structurally can't provide; otherwise renders fast and fills in
  // suggestions in the background.
  private async refreshProducts() {
    const token = ++this.productsRequestToken;
    this.reorderSuggestionsLoaded = false;
    await this.ensureSupplierLoaded();
    if (token !== this.productsRequestToken) { return; } // superseded mid supplier-load

    if (this.isReportOnlyFlow) {
      await this.fetchProductsViaReportOnly(token);
      return;
    }

    await this.fetchProductsFast(token);
    if (token !== this.productsRequestToken) { return; } // superseded mid Phase-1 fetch
    this.maybeShowReassurance();
    this.fetchReorderSuggestions(token); // not awaited — runs in the background
  }

  private formatDate(value: any): string {
    if (!value) return '-';
    
    try {
      // If it's a timestamp (number)
      if (typeof value === 'number') {
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      // If it's a string, try to parse it
      if (typeof value === 'string') {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '-'; // Invalid date
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      // If it's already a Date object
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  private formatNumber(value: any, decimals: number = 2): string {
    if (value === null || value === undefined) return '0.00';
    if (typeof value === 'string') value = parseFloat(value);
    if (typeof value !== 'number' || isNaN(value)) return '0.00';
    return value.toFixed(decimals);
  }

  private formatCurrency(value: number): string {
    return this.formatSupplierCurrency(value);
  }

  private getStockLevelColor(product: any): string {
    const stockLevel = product['[Actual Inventory Quantity]'] || product.currentInventory;
    const reorderQuantity = product['[inventory_replenishment]'] || product.reorderQuantity;
    
    // If we have less than half of the recommended reorder quantity, it's critical
    if (stockLevel <= (reorderQuantity * 0.5)) return 'error';
    
    // If we have less than the recommended reorder quantity, it's a warning
    if (stockLevel <= reorderQuantity) return 'warning';
    
    // Otherwise, we're good
    return 'success';
  }

  private updateItemTotal(item: any) {
    item.userEdited = true;
    const quantity = Number(item.quantity) || 0;
    const editedCost = item['[average_unit_cost]'];
    const originalCost = item.original_average_unit_cost || 0;
    let effectiveCost = originalCost;

    if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
      const numEditedCost = Number(editedCost);
      if (!isNaN(numEditedCost) && numEditedCost >= 0) {
        effectiveCost = numEditedCost;
        if (item.cost_source === 'converted' && numEditedCost !== originalCost) {
          item.cost_source = 'manual';
        }
      }
    }

    this.$set(item, 'total', quantity * effectiveCost);
  }

  private clearSelection() {
    this.selectionMap.clear();
    this.selectionVersion++;
    this.showClearSelectionConfirm = false;
  }

  private armReassurance() {
    if (this.selectedProducts.length === 0) { return; }
    if (sessionStorage.getItem(PurchaseOrderCreateView.REASSURE_KEY) === '1') { return; }
    this.shouldReassureOnNextFetch = true;
  }

  private onAddToPurchaseListOpen(): void {
    if (!this.selectedProducts.length) { return; }
    this.addToPurchaseListOpen = true;
  }

  // US-090: products picked in the Add Products dialog. They join the same selectionMap
  // the suggestion list writes to, so the Order Summary, validation and
  // submitPurchaseOrder() need no special case — and pinSelectedFirst() surfaces them at
  // the top of the table straight away.
  private onProductsSelected(products: any[]): void {
    const rows = buildSearchAddedRows(products || [], (variantId: number) => ({
      ...this.costLadderCommonOpts,
      supplierCost: this.supplierCostMap.get(variantId) ?? null,
    }));
    if (!rows.length) { return; }
    // Empty visibleIds => pure addition: nothing is removed, and existing rows keep
    // their stored reference so in-place quantity/cost edits survive.
    applyTableSelectionDiff(this.selectionMap, rows, new Set());
    this.selectionVersion++;
    EventBus.$emit('show-snackbar', {
      message: `Added ${rows.length} product${rows.length > 1 ? 's' : ''} to this purchase order.`,
      color: 'success',
    });
  }

  private onAddToPurchaseListAdded(): void {
    // Non-destructive by default: the dialog has already fired its own
    // success snackbar ("Added N item(s) to purchase list") via its submit()
    // method, so we do nothing here. The user's on-page selection stays
    // intact and they can still submit this PO.
  }

  get storeCurrency(): string {
    return CurrencyHelper.getStoreCurrency();
  }

  get supplierCurrencyCode(): string {
    return this.supplier?.currency_code || this.storeCurrency;
  }

  get isForeignCurrency(): boolean {
    const supplierCurr = this.supplier?.currency_code;
    return !!supplierCurr && supplierCurr !== this.storeCurrency;
  }

  get currencySymbol(): string {
    return CurrencyHelper.getCurrencySymbol(this.supplierCurrencyCode);
  }

  get currSuffix(): string {
    return this.isForeignCurrency ? ` (${this.supplierCurrencyCode})` : '';
  }

  get currencyStep(): string {
    return CurrencyHelper.getCurrencyStep(this.supplierCurrencyCode);
  }

  get exchangeRateDisplay(): string | null {
    if (!this.isForeignCurrency || !this.exchangeRate) return null;
    return `1 ${this.supplierCurrencyCode} = ${Number(this.exchangeRate).toFixed(6)} ${this.storeCurrency}`;
  }

  formatSupplierCurrency(value: number | null | undefined): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.supplierCurrencyCode);
  }

  formatStoreCurrency(value: number | null | undefined): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.storeCurrency);
  }

  grandTotalInStore(): number | null {
    if (!this.isForeignCurrency || !this.exchangeRate) return null;
    const decimals = CurrencyHelper.getDecimalPlaces(this.storeCurrency);
    const factor = Math.pow(10, decimals);
    return Math.round(this.totalValue * this.exchangeRate * factor) / factor;
  }

  get subtotalValue(): number {
    return this.selectedProducts.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const editedCost = item['[average_unit_cost]'];
      const originalCost = item.original_average_unit_cost || 0;
      let effectiveCost = originalCost;

      if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
        const numEditedCost = Number(editedCost);
        if (!isNaN(numEditedCost) && numEditedCost >= 0) {
          effectiveCost = numEditedCost;
        }
      }

      return sum + (quantity * effectiveCost);
    }, 0);
  }

  private roundForCurrency(value: number): number {
    return CurrencyHelper.roundForCurrency(value, this.supplierCurrencyCode);
  }

  get shippingTaxAmount(): number {
    const cost = Math.max(Number(this.shippingCost) || 0, 0);
    const rate = Math.min(Math.max(Number(this.shippingTaxRate) || 0, 0), 100);
    return this.roundForCurrency(cost * (rate / 100));
  }

  get computedDiscountCalculated(): number {
    const amount = Number(this.discountAmount) || 0;
    if (amount <= 0) return 0;
    if (this.discountType === 'percentage') {
      const clampedPercent = Math.min(Math.max(amount, 0), 100);
      return this.roundForCurrency(this.subtotalValue * clampedPercent / 100);
    }
    return this.roundForCurrency(Math.min(amount, this.subtotalValue));
  }

  get hasAdjustment(): boolean {
    return (Number(this.adjustmentAmount) || 0) !== 0;
  }

  get getItemTaxTotal(): number {
    return this.selectedProducts.reduce((sum: number, item: any) => {
      return sum + (Number(item.extra?.financial?.tax_amount) || 0);
    }, 0);
  }

  get totalValue(): number {
    const shipping = Math.max(Number(this.shippingCost) || 0, 0);
    const itemTax = this.getItemTaxTotal;
    const discountCalc = this.computedDiscountCalculated;
    const adjustment = Number(this.adjustmentAmount) || 0;
    return this.roundForCurrency(this.subtotalValue + itemTax + shipping + this.shippingTaxAmount - discountCalc + adjustment);
  }

  private resolveSupplierDefaultTaxType() {
    // Try supplier's default tax type first
    const defaultId = this.supplier?.options?.financial?.default_tax_type_id;
    if (defaultId) {
      // Use == to handle type mismatch (options JSON may store id as string)
      this.supplierDefaultTaxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id == defaultId) || null;
    }

    // Fall back to the merchant's default tax type
    if (!this.supplierDefaultTaxType) {
      this.supplierDefaultTaxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.isDefault) || null;
    }
  }

  get isValid(): boolean {
    if (!this.purchaseOrder.poNumber || 
        !this.purchaseOrder.orderDate ||
        !this.purchaseOrder.locationId ||
        !this.supplier ||
        this.selectedProducts.length === 0) {
      return false;
    }

    if (!this.isValidOrderValue) {
      return false;
    }

    return this.selectedProducts.every(item => {
      const quantity = Number(item.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        return false; 
      }

      const editedCost = item['[average_unit_cost]'];
      const originalCost = item.original_average_unit_cost || 0;
      let effectiveCost = -1; 

      if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
        const numEditedCost = Number(editedCost);
        if (!isNaN(numEditedCost) && numEditedCost >= 0) {
          effectiveCost = numEditedCost;
        } else {
          return false; 
        }
      } else {
          effectiveCost = originalCost;
      }
      
      if (effectiveCost < 0) {
          return false;
      }
      
      return true; 
    });
  }

  get isValidOrderValue(): boolean {
    if (!this.supplier) return false;
    const minOrder = Number(this.supplier.minOrderValue);
    const maxOrder = Number(this.supplier.maxOrderValue);
    if (this.totalValue < minOrder && minOrder > 0) return false;
    if (this.totalValue > maxOrder && maxOrder > 0) return false;
    return true;
  }

  get getOrderValueStatus(): string {
    if (!this.supplier) return 'grey';
    const minOrder = Number(this.supplier.minOrderValue);
    const maxOrder = Number(this.supplier.maxOrderValue);

    if (this.totalValue < minOrder && minOrder > 0) return 'error';
    if (this.totalValue > maxOrder && maxOrder > 0) return 'error';
    return 'success';
  }

  get getOrderValueMessage(): string {
    if (!this.supplier) return 'Invalid';
    const minOrder = Number(this.supplier.minOrderValue);
    const maxOrder = Number(this.supplier.maxOrderValue);

    if (this.totalValue < minOrder && minOrder > 0) {
      return `Below Minimum (${this.formatSupplierCurrency(this.totalValue)} < ${this.formatSupplierCurrency(minOrder)})`;
    }
    if (this.totalValue > maxOrder && maxOrder > 0) {
      return `Exceeds Maximum (${this.formatSupplierCurrency(this.totalValue)} > ${this.formatSupplierCurrency(maxOrder)})`;
    }
    return `Valid Order Value (${this.formatSupplierCurrency(this.totalValue)})`;
  }

  get selectedProducts(): any[] {
    void this.selectionVersion; // establish reactive dep on Map mutations
    return Array.from(this.selectionMap.values());
  }

  // US-090: what the Add Products dialog must leave out of its results. Ids rather than
  // rows, because the two pages hold their PO lines in different shapes — this basket
  // holds table rows, Edit-PO holds IPurchaseOrderItem.
  get selectedVariantIds(): number[] {
    return this.selectedProducts.map((p: any) => Number(p.variant_id));
  }

  get tableSelection(): any[] {
    // Intersection of the Map with currently-displayed rows — this is what
    // Vuetify renders as checkbox state on the current page/filter.
    void this.selectionVersion; // establish reactive dep on Map mutations
    return this.filteredProducts.filter(
      (p: any) => this.selectionMap.has(Number(p.variant_id)),
    );
  }

  set tableSelection(next: any[]) {
    const visibleIds = new Set<number>(
      this.filteredProducts.map((p: any) => Number(p.variant_id)),
    );
    applyTableSelectionDiff(this.selectionMap, next, visibleIds);
    this.selectionVersion++;
  }

  private get currentAddToPurchaseListStrategy() {
    const supplierId = Number(this.supplier?.id ?? 0);
    const selectedRows = this.selectedProducts;
    return () => supplierProductsStrategy({ supplierId, selectedRows });
  }

  get filteredProducts(): any[] {
    if (this.filterType === 'selected') {
      // Bypass server-side pagination — render every selected row regardless
      // of which page it was selected on. The Map already holds full row data
      // by reference (preserved by the toFastRow/fetchProductsViaReportOnly merge rule).
      return this.selectedProducts;
    }
    // US-090: products already on the PO sit at the top of the same table, with the
    // browsable suggestion catalog below them — so a product added through the
    // Add Products search dialog is visible immediately even though it usually
    // isn't on the catalog's current server-paginated page.
    return pinSelectedFirst(this.selectedProducts, this.availableProducts, this.selectionMap);
  }

  get locationOptions() {
    return [
      { id: null, name: 'Select Delivery Location' },
      ...this.locations.map((loc: StoreLocationModel) => ({ id: loc.id, name: loc.name }))
    ];
  }

  private async handleSearchTrigger() {
    this.armReassurance();
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1;
    } else {
      await this.refreshProducts();
    }
  }

  private async submitPurchaseOrder() {
    this.submitting = true;

    if (!this.isValid) {
      let errorMessages: string[] = [];
      if (!this.purchaseOrder.poNumber) {
        errorMessages.push('PO Number is required.');
      }
      if (!this.purchaseOrder.orderDate) {
        errorMessages.push('Order Date is required.');
      }
      if (!this.purchaseOrder.locationId) {
        errorMessages.push('Delivery Location is required.');
      }
      if (!this.supplier) {
        errorMessages.push('Supplier information is missing.');
      }
      if (this.selectedProducts.length === 0) {
        errorMessages.push('At least one product must be selected.');
      }

      this.selectedProducts.forEach(item => {
        const quantity = Number(item.quantity);
        if (isNaN(quantity) || quantity <= 0) {
          errorMessages.push(`Invalid quantity for product '${item['Item[Item Description]'] || item.variant_id}'. Quantity must be greater than 0.`);
        }

        const editedCost = item['[average_unit_cost]'];
        if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
          const numEditedCost = Number(editedCost);
          if (isNaN(numEditedCost) || numEditedCost < 0) {
            errorMessages.push(`Invalid unit cost for product '${item['Item[Item Description]'] || item.variant_id}'. Cost must be a non-negative number.`);
          }
        }
        // If editedCost is empty, it's handled by the original_average_unit_cost fallback in `isValid` implicitly,
        // so we only check for actively invalid entries here.
      });
      
      if (!this.isValidOrderValue) {
        const orderValueMessage = this.getOrderValueMessage;
        if (this.getOrderValueStatus === 'error') { // Check if it's an actual error state
             errorMessages.push(`Order total issue: ${orderValueMessage}. Please adjust quantities or unit costs.`);
        }
      }

      if (errorMessages.length > 0) {
        EventBus.$emit("show-snackbar", { message: `Cannot submit PO: ${errorMessages.join(' ')}`, color: "error" });
        this.submitting = false;
        return;
      }
    }
    
    try {
      // Build financial data
      const shippingCostVal = Math.max(Number(this.shippingCost) || 0, 0);
      const shippingTaxRateVal = Math.min(Math.max(Number(this.shippingTaxRate) || 0, 0), 100) / 100; // Convert percentage to decimal, clamped 0-100
      const rawDiscount = Number(this.discountAmount) || 0;
      const discountAmountVal = this.discountType === 'percentage'
        ? Math.min(Math.max(rawDiscount, 0), 100)
        : Math.min(Math.max(rawDiscount, 0), this.subtotalValue);
      const adjustmentAmountVal = Number(this.adjustmentAmount) || 0;
      const hasFinancialData = shippingCostVal > 0 || discountAmountVal > 0 || adjustmentAmountVal !== 0;
      const financialData = hasFinancialData ? {
        ...(shippingCostVal > 0 ? { shipping_cost: shippingCostVal, shipping_tax_rate: shippingTaxRateVal } : {}),
        ...(discountAmountVal > 0 ? { discount_amount: discountAmountVal, discount_type: this.discountType } : {}),
        ...(adjustmentAmountVal !== 0 ? { adjustment_amount: adjustmentAmountVal, adjustment_note: this.adjustmentNote || '' } : {}),
      } : undefined;

      const poToSubmit = new PurchaseOrderModel({
        ...this.purchaseOrder,
        currencyCode: this.supplierCurrencyCode,
        locationId: this.purchaseOrder.locationId,
        status: 'draft',
        extra: {
          ...this.purchaseOrder.extra,
          ...(financialData ? { financial: financialData } : {}),
        },
        items: this.selectedProducts.map(product => {
          const orderedQuantity = Number(product.quantity) || 0;
          const editedCost = product['[average_unit_cost]'];
          const originalCost = product.original_average_unit_cost || 0;
          let effectiveUnitPrice = originalCost;

          if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
            const numEditedCost = Number(editedCost);
            if (!isNaN(numEditedCost) && numEditedCost >= 0) {
              effectiveUnitPrice = numEditedCost;
            }
          }
          // Auto-fill tax from supplier's default tax type
          const taxExtra = this.supplierDefaultTaxType ? {
            financial: {
              tax_type_id: this.supplierDefaultTaxType.id,
              tax_type_name: this.supplierDefaultTaxType.name,
              tax_rate: this.supplierDefaultTaxType.rate,
              tax_amount: this.roundForCurrency(orderedQuantity * effectiveUnitPrice * this.supplierDefaultTaxType.rate),
            }
          } : undefined;

          return {
            variantId: product.variant_id,
            variantImage: product.variant_image,
            productName: product['Item[Item Description]'],
            variant: product.variant,
            sku: product.product_sku,
            supplierSku: product.supplier_sku || product.supplierSku || undefined,
            orderedQuantity: orderedQuantity,
            receivedQuantity: 0,
            unitPrice: effectiveUnitPrice,
            totalPrice: orderedQuantity * effectiveUnitPrice,
            extra: taxExtra,
          };
        })
      });

      await poToSubmit.save();
      this.$emit('success', 'Purchase Order submitted successfully');

      // Attempt review request after successful PO creation (non-blocking)
      reviewRequestService.attemptReviewRequest(
        this.store.id,
        'po_created'
      );

      this.$router.push(`/purchase-orders/${poToSubmit.id}`);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const message = error.response?.data?.message || 'PO number already exists. Please use a different PO number.';
        EventBus.$emit("show-snackbar", { message, color: "error" });
      } else {
        this.$emit('error', 'Failed to submit Purchase Order');
      }
    } finally {
      this.submitting = false;
    }
  }

  // New Computed Properties for Forecasting - REMOVED (moved to InventoryForecasting.vue)
  /*
  get methodologyOptions(): Array<{ text: string, value: string, description: string }> {
    const baseOptions = [
      {
        text: 'Simple average moving',
        value: 'simple_average',
        description: 'Uses a straightforward average of historical data. Best for stable data.'
      },
      {
        text: 'Computing model',
        value: 'combined_weighted',
        description: 'Combines moving averages with yearly trends. Ideal for seasonal data.'
      }
    ];

    if (this.store && typeof this.store.isValidateRule === 'function' && this.store.isValidateRule(FEATURES.AI_ML_DEMAND_FORECASTING, 'unlocked')) {
      baseOptions.push({
        text: 'Smart model (using AI)',
        value: 'ai',
        description: 'AI-powered model for complex, dynamic data patterns. Date range not required for suggestion.'
      });
    }
    return baseOptions;
  }

  get currentMethodDescription(): string {
    const selectedMethod = this.methodologyOptions.find(m => m.value === this.selectedForecastingMethod);
    return selectedMethod ? selectedMethod.description : 'Select a forecasting method to see its description.';
  }

  get showDateFilter(): boolean {
    // Show date filter only if a method is selected and it's not the AI model
    return !!this.selectedForecastingMethod && 
           (this.selectedForecastingMethod === 'simple_average' || this.selectedForecastingMethod === 'combined_weighted');
  }
  */

  // Renamed to avoid conflict if child emits same name, or just reuse.
  private navigateToForecastSettings(): void {
    this.$router.push('/settings/inventory-forecast');
  }

  // Event handlers for InventoryForecasting component
  private handleDatesUpdated(newDates: string[]): void {
    this.dates = newDates;
    // The existing @Watch('dates') will trigger refreshProducts
  }

  private handleMethodApplied(): void {
    // Method saved by child, parent just needs to refetch data
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1; // Reset to first page if method changes
    } else {
      this.refreshProducts();
    }
  }

  private handleForecastError(message: string): void {
    this.$emit('error', message);
  }

  private handleForecastSuccess(message: string): void {
    this.$emit('success', message);
  }

  // + Add new method to handle emitted filter list
  private handleLocationFilterUpdate(filterList: FilterColumnList): void {
    this.armReassurance();
    this.activeLocationFilters = filterList;
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1;
    } else {
      this.refreshProducts();
    }
  }

}
